import React, { useState } from 'react';
import { Card, Form, Select, Switch, Button, Typography, Divider, Space, message, Tabs, Checkbox } from 'antd';
import { SaveOutlined, BgColorsOutlined, BellOutlined, GlobalOutlined, SecurityScanOutlined, ThunderboltOutlined, WhatsAppOutlined, PhoneOutlined } from '@ant-design/icons';
import useThemeStore from '../store/themeStore';
import useAuthStore from '../store/authStore';

const { Title, Text } = Typography;
const { Option } = Select;

const SettingsPage = () => {
  const { theme, setTheme } = useThemeStore();
  const { user, updateProfile, isLoading } = useAuthStore();
  const [form] = Form.useForm();

  const handleSettingsUpdate = async (values) => {
    const result = await updateProfile({
      preferences: {
        ...user?.preferences,
        ...values
      }
    });
    
    if (result.success) {
      message.success('Settings updated successfully');
    } else {
      message.error(result.error || 'Failed to update settings');
    }
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    message.success(`Theme changed to ${newTheme}`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Title level={2} className="mb-2">Settings</Title>
        <Text className="text-gray-600 dark:text-gray-400">
          Customize your CRM experience and preferences
        </Text>
      </div>

      <Tabs 
        defaultActiveKey="appearance" 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-soft p-6"
        items={[
          {
            key: 'appearance',
            label: (
              <span>
                <BgColorsOutlined />
                Appearance
              </span>
            ),
            children: (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <Text className="block font-medium">Theme</Text>
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  Choose your preferred color scheme
                </Text>
              </div>
              <Select
                value={theme}
                onChange={handleThemeChange}
                style={{ width: 120 }}
                className="focus-visible:focus"
              >
                <Option value="light">Light</Option>
                <Option value="dark">Dark</Option>
                <Option value="system">System</Option>
              </Select>
            </div>
          </div>
            )
          },
          {
            key: 'notifications',
            label: (
              <span>
                <BellOutlined />
                Notifications
              </span>
            ),
            children: (
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              emailNotifications: user?.preferences?.notifications?.email ?? true,
              pushNotifications: user?.preferences?.notifications?.push ?? true,
              leadNotifications: true,
              dealNotifications: true,
              taskReminders: true,
              weeklyReports: true
            }}
            onFinish={handleSettingsUpdate}
          >
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Email Notifications</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Receive notifications via email
                  </Text>
                </div>
                <Form.Item name="emailNotifications" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>

              <Divider className="my-4" />

              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Push Notifications</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Receive browser push notifications
                  </Text>
                </div>
                <Form.Item name="pushNotifications" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>

              <Divider className="my-4" />

              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">New Lead Notifications</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Get notified when new leads are created
                  </Text>
                </div>
                <Form.Item name="leadNotifications" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Deal Updates</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Get notified about deal stage changes
                  </Text>
                </div>
                <Form.Item name="dealNotifications" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Task Reminders</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Receive reminders for upcoming tasks
                  </Text>
                </div>
                <Form.Item name="taskReminders" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Weekly Reports</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Receive weekly performance summaries
                  </Text>
                </div>
                <Form.Item name="weeklyReports" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>
            </div>

            <Divider className="my-6" />

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={isLoading}
                icon={<SaveOutlined />}
                className="focus-visible:focus"
              >
                Save Notification Settings
              </Button>
            </Form.Item>
          </Form>
            )
          },
          {
            key: 'regional',
            label: (
              <span>
                <GlobalOutlined />
                Regional
              </span>
            ),
            children: (
          <Form
            layout="vertical"
            initialValues={{
              language: user?.preferences?.language || 'en',
              timezone: user?.preferences?.timezone || 'UTC',
              dateFormat: 'MM/DD/YYYY',
              currency: 'USD'
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="language"
                label="Language"
              >
                <Select className="focus-visible:focus">
                  <Option value="en">English</Option>
                  <Option value="es">Spanish</Option>
                  <Option value="fr">French</Option>
                  <Option value="de">German</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="timezone"
                label="Timezone"
              >
                <Select 
                  showSearch
                  placeholder="Select timezone"
                  className="focus-visible:focus"
                >
                  <Option value="UTC">UTC</Option>
                  <Option value="America/New_York">Eastern Time</Option>
                  <Option value="America/Chicago">Central Time</Option>
                  <Option value="America/Denver">Mountain Time</Option>
                  <Option value="America/Los_Angeles">Pacific Time</Option>
                  <Option value="Europe/London">London</Option>
                  <Option value="Europe/Paris">Paris</Option>
                  <Option value="Asia/Tokyo">Tokyo</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="dateFormat"
                label="Date Format"
              >
                <Select className="focus-visible:focus">
                  <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                  <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                  <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="currency"
                label="Currency"
              >
                <Select className="focus-visible:focus">
                  <Option value="USD">USD ($)</Option>
                  <Option value="EUR">EUR (€)</Option>
                  <Option value="GBP">GBP (£)</Option>
                  <Option value="JPY">JPY (¥)</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit"
                icon={<SaveOutlined />}
                className="focus-visible:focus"
              >
                Save Regional Settings
              </Button>
            </Form.Item>
          </Form>
            )
          },
          {
            key: 'privacy',
            label: (
              <span>
                <SecurityScanOutlined />
                Privacy
              </span>
            ),
            children: (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <Text className="block font-medium">Two-Factor Authentication</Text>
                    <Text className="text-sm text-gray-500 dark:text-gray-400">
                      Add an extra layer of security to your account
                    </Text>
                  </div>
                  <Button type="primary" ghost className="focus-visible:focus">
                    Enable 2FA
                  </Button>
                </div>

                <Divider />

                <div className="flex justify-between items-center">
                  <div>
                    <Text className="block font-medium">Session Management</Text>
                    <Text className="text-sm text-gray-500 dark:text-gray-400">
                      View and manage your active sessions
                    </Text>
                  </div>
                  <Button className="focus-visible:focus">
                    Manage Sessions
                  </Button>
                </div>

                <Divider />

                <div className="flex justify-between items-center">
                  <div>
                    <Text className="block font-medium">Data Export</Text>
                    <Text className="text-sm text-gray-500 dark:text-gray-400">
                      Download your data in various formats
                    </Text>
                  </div>
                  <Button className="focus-visible:focus">
                    Export Data
                  </Button>
                </div>
              </div>
            )
          },
          {
            key: 'automation',
            label: (
              <span>
                <ThunderboltOutlined />
                Automation
              </span>
            ),
            children: (
              <Form
                layout="vertical"
                initialValues={{
                  whatsappEnabled: user?.preferences?.automation?.whatsappEnabled ?? false,
                  aiCallingEnabled: user?.preferences?.automation?.aiCallingEnabled ?? false
                }}
                onFinish={handleSettingsUpdate}
              >
                <div className="space-y-6">
                  <div>
                    <Title level={5} className="mb-4">
                      <ThunderboltOutlined className="mr-2" />
                      Communication Automation
                    </Title>
                    <Text className="text-gray-600 dark:text-gray-400 block mb-6">
                      Enable automated communication features to streamline your workflow
                    </Text>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-start p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <WhatsAppOutlined className="text-green-500 text-xl mr-2" />
                          <Text className="font-medium text-lg">WhatsApp Integration</Text>
                        </div>
                        <Text className="text-sm text-gray-500 dark:text-gray-400 block mb-2">
                          Enable WhatsApp messaging for lead communication and follow-ups
                        </Text>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-xs rounded">
                            Instant Messaging
                          </span>
                          <span className="px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-xs rounded">
                            Media Sharing
                          </span>
                          <span className="px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-xs rounded">
                            Status Updates
                          </span>
                        </div>
                      </div>
                      <Form.Item name="whatsappEnabled" valuePropName="checked" className="mb-0 ml-4">
                        <Switch 
                          checkedChildren="ON" 
                          unCheckedChildren="OFF"
                          className="bg-gray-300"
                        />
                      </Form.Item>
                    </div>

                    <div className="flex justify-between items-start p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <PhoneOutlined className="text-blue-500 text-xl mr-2 icon-flip-horizontal" />
                          <Text className="font-medium text-lg">AI Calling</Text>
                        </div>
                        <Text className="text-sm text-gray-500 dark:text-gray-400 block mb-2">
                          Enable AI-powered calling for automated lead outreach and qualification
                        </Text>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs rounded">
                            Auto Dialing
                          </span>
                          <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs rounded">
                            Voice Recognition
                          </span>
                          <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs rounded">
                            Call Analytics
                          </span>
                        </div>
                      </div>
                      <Form.Item name="aiCallingEnabled" valuePropName="checked" className="mb-0 ml-4">
                        <Switch 
                          checkedChildren="ON" 
                          unCheckedChildren="OFF"
                          className="bg-gray-300"
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <Divider className="my-6" />

                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <Text className="text-sm text-blue-600 dark:text-blue-400">
                      <strong>Note:</strong> Automation features may require additional configuration and API keys. 
                      Contact your administrator for setup assistance.
                    </Text>
                  </div>
                </div>

                <Divider className="my-6" />

                <Form.Item>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={isLoading}
                    icon={<SaveOutlined />}
                    className="focus-visible:focus"
                  >
                    Save Automation Settings
                  </Button>
                </Form.Item>
              </Form>
            )
          }
        ]}
      />
    </div>
  );
};

export default SettingsPage;
